import React, { useState } from 'react';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { Organization, UserProfile, VicePrincipalRecord } from '../types';
import { Avatar } from './common';
import { TransferOwnershipModal } from './TransferOwnershipModal';

interface VicePrincipalsModalProps {
  onClose: () => void;
  org: Organization;
  currentUser: UserProfile;
  allUsers: UserProfile[];
  onUpdateOrg: (orgId: string, data: Partial<Organization>) => Promise<void> | void;
  onUpdateUser: (userId: string, data: Partial<UserProfile>) => Promise<void> | void;
  onTransferOwnership?: (params: {
    orgId: string;
    newAdminEmail: string;
    newAdminId?: string;
    newAdminName?: string;
    outgoingAdminRole: 'vice_principal' | 'teacher' | 'stepped_down';
    reason?: string;
    referenceTicket?: string;
    transferType?: 'self_serve' | 'super_admin_reassignment';
  }) => Promise<void>;
  t?: (key: string, reps?: any) => string;
}

export const VicePrincipalsModal: React.FC<VicePrincipalsModalProps> = ({
  onClose,
  org,
  currentUser,
  allUsers,
  onUpdateOrg,
  onUpdateUser,
  onTransferOwnership,
  t
}) => {
  const [emailInput, setEmailInput] = useState('');
  const [nameInput, setNameInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [removingEmail, setRemovingEmail] = useState<string | null>(null);
  const [copyToast, setCopyToast] = useState<string | null>(null);
  const [showTransferModal, setShowTransferModal] = useState(false);

  const isPrimaryPrincipal = currentUser.id === org.adminId;
  const primaryAdmin = allUsers.find(u => u.id === org.adminId) || (isPrimaryPrincipal ? currentUser : null);

  // Normalize list of vice principals from org data
  const vicePrincipalEmails: string[] = org.vicePrincipalEmails || [];
  const vicePrincipals: VicePrincipalRecord[] = org.vicePrincipals || [];

  // Match each email to either an existing record or user profile
  const adminList = React.useMemo(() => {
    return vicePrincipalEmails.map(email => {
      const lowerEmail = email.toLowerCase().trim();
      const existingRecord = vicePrincipals.find(v => v.email.toLowerCase().trim() === lowerEmail);
      const matchedUser = allUsers.find(u => u.email?.toLowerCase().trim() === lowerEmail);
      
      return {
        email: lowerEmail,
        name: matchedUser?.name || existingRecord?.name || 'School Administrator',
        photoUrl: matchedUser?.photoUrl,
        userId: matchedUser?.id || existingRecord?.userId,
        addedAt: existingRecord?.addedAt,
        isActive: !!matchedUser && (matchedUser.role === 'principal' || matchedUser.isVicePrincipal),
        userProfile: matchedUser
      };
    });
  }, [vicePrincipalEmails, vicePrincipals, allUsers]);

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleAddVicePrincipal = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    const cleanEmail = emailInput.toLowerCase().trim();
    if (!cleanEmail) {
      setError('Please enter a valid email address.');
      return;
    }

    if (!validateEmail(cleanEmail)) {
      setError('Please enter a valid email format (e.g. colleague@gmail.com).');
      return;
    }

    // Check if it's the primary principal's email
    if (primaryAdmin && primaryAdmin.email?.toLowerCase().trim() === cleanEmail) {
      setError('This email belongs to the primary Principal.');
      return;
    }

    if (currentUser.email?.toLowerCase().trim() === cleanEmail) {
      setError('You are already the active administrator.');
      return;
    }

    // Check if already in vice principals list
    if (vicePrincipalEmails.some(e => e.toLowerCase().trim() === cleanEmail)) {
      setError('This email is already added as a Vice Principal.');
      return;
    }

    setIsProcessing(true);
    try {
      // Find if user already exists in allUsers or directly in Firestore
      let targetUserId: string | undefined = undefined;
      let targetUserName: string = nameInput.trim();

      const existingUser = allUsers.find(u => u.email?.toLowerCase().trim() === cleanEmail);
      if (existingUser) {
        targetUserId = existingUser.id;
        if (!targetUserName) targetUserName = existingUser.name;
      } else {
        // Query Firestore users collection
        try {
          const uq = query(collection(db, 'users'), where('email', '==', cleanEmail), limit(1));
          const uSnap = await getDocs(uq);
          if (!uSnap.empty) {
            targetUserId = uSnap.docs[0].id;
            const uData = uSnap.docs[0].data();
            if (!targetUserName) targetUserName = uData.name || '';
          }
        } catch (queryErr) {
          console.warn("Direct user query note:", queryErr);
        }
      }

      const newRecord: VicePrincipalRecord = {
        email: cleanEmail,
        name: targetUserName,
        userId: targetUserId,
        addedAt: new Date().toISOString(),
        addedBy: currentUser.id
      };

      const updatedEmails = Array.from(new Set([...vicePrincipalEmails, cleanEmail]));
      const updatedRecords = [
        ...vicePrincipals.filter(v => v.email.toLowerCase().trim() !== cleanEmail),
        newRecord
      ];

      // Update organization in Firestore
      await onUpdateOrg(org.id, {
        vicePrincipalEmails: updatedEmails,
        vicePrincipals: updatedRecords
      });

      // If user document exists, update their role to principal immediately
      if (targetUserId) {
        await onUpdateUser(targetUserId, {
          role: 'principal',
          orgId: org.id,
          approvalStatus: 'approved',
          isVicePrincipal: true
        });
      }

      setEmailInput('');
      setNameInput('');
      setSuccessMessage(`${cleanEmail} has been granted Vice Principal administrative privileges.`);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      console.error('Error adding vice principal:', err);
      setError(err?.message || 'Failed to add Vice Principal. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmRemove = async (emailToRemove: string) => {
    setIsProcessing(true);
    setError(null);
    setSuccessMessage(null);

    const cleanEmail = emailToRemove.toLowerCase().trim();
    try {
      const updatedEmails = vicePrincipalEmails.filter(e => e.toLowerCase().trim() !== cleanEmail);
      const updatedRecords = vicePrincipals.filter(v => v.email.toLowerCase().trim() !== cleanEmail);

      // Update organization
      await onUpdateOrg(org.id, {
        vicePrincipalEmails: updatedEmails,
        vicePrincipals: updatedRecords
      });

      // If matching user exists in system, revoke principal privileges
      let targetUserId: string | undefined = undefined;
      const matchedUser = allUsers.find(u => u.email?.toLowerCase().trim() === cleanEmail);
      if (matchedUser) {
        targetUserId = matchedUser.id;
      } else {
        try {
          const uq = query(collection(db, 'users'), where('email', '==', cleanEmail), limit(1));
          const uSnap = await getDocs(uq);
          if (!uSnap.empty) {
            targetUserId = uSnap.docs[0].id;
          }
        } catch (err) {
          console.warn("Direct user query note during removal:", err);
        }
      }

      if (targetUserId) {
        await onUpdateUser(targetUserId, {
          isVicePrincipal: false,
          approvalStatus: 'removed'
        });
      }

      setRemovingEmail(null);
      setSuccessMessage(`Removed administrative privileges for ${cleanEmail}.`);
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err: any) {
      console.error('Error removing vice principal:', err);
      setError(err?.message || 'Failed to remove Vice Principal. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const copyInvitationText = (email: string) => {
    const inviteText = `Hi! You have been designated as Vice Principal / Co-Admin for ${org.name} on EduGravity. Please sign in with your Google account (${email}) at ${window.location.origin} to access the administrative dashboard.`;
    navigator.clipboard.writeText(inviteText);
    setCopyToast(`Invitation note copied for ${email}`);
    setTimeout(() => setCopyToast(null), 3000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl overflow-hidden border border-gray-100 my-8">
        
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white p-6 flex justify-between items-start">
          <div className="flex items-center gap-3.5">
            <div className="w-12 h-12 rounded-xl bg-indigo-500/20 border border-indigo-400/30 flex items-center justify-center text-indigo-300">
              <span className="material-icons-round text-3xl">supervisor_account</span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold">Vice Principals & Co-Admins</h2>
                <span className="bg-indigo-500/30 text-indigo-200 text-xs px-2 py-0.5 rounded-full font-medium border border-indigo-400/20">
                  {adminList.length + 1} Total Admins
                </span>
              </div>
              <p className="text-xs text-indigo-200 mt-1">
                Authorize additional leaders to manage {org.name} when you are away.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-full transition-colors"
            title="Close"
          >
            <span className="material-icons-round text-xl">close</span>
          </button>
        </div>

        <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">
          
          {/* Status Messages */}
          {copyToast && (
            <div className="bg-emerald-50 text-emerald-800 border border-emerald-200 px-4 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 animate-fade-in">
              <span className="material-icons-round text-base text-emerald-600">content_copy</span>
              <span>{copyToast}</span>
            </div>
          )}

          {error && (
            <div className="bg-red-50 text-red-700 border border-red-200 px-4 py-3 rounded-xl text-xs font-semibold flex items-center gap-2">
              <span className="material-icons-round text-base text-red-500">error_outline</span>
              <span className="flex-1">{error}</span>
              <button onClick={() => setError(null)} className="text-red-400 hover:text-red-700">
                <span className="material-icons-round text-sm">close</span>
              </button>
            </div>
          )}

          {successMessage && (
            <div className="bg-emerald-50 text-emerald-800 border border-emerald-200 px-4 py-3 rounded-xl text-xs font-semibold flex items-center gap-2">
              <span className="material-icons-round text-base text-emerald-600">check_circle</span>
              <span className="flex-1">{successMessage}</span>
            </div>
          )}

          {/* If current user is not primary principal, show informational banner */}
          {!isPrimaryPrincipal && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-800 flex items-start gap-2.5">
              <span className="material-icons-round text-amber-600 text-lg">info</span>
              <div>
                <p className="font-bold">You are signed in as a Vice Principal</p>
                <p className="text-amber-700 mt-0.5">
                  Only the primary Principal ({primaryAdmin?.name || 'Account Owner'}) can add or remove administrative delegates.
                </p>
              </div>
            </div>
          )}

          {/* Simple Email Input Card (Only for Primary Principal) */}
          {isPrimaryPrincipal && (
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 shadow-xs">
              <div className="flex items-center gap-2 mb-3">
                <span className="material-icons-round text-indigo-600 text-lg">person_add</span>
                <h3 className="font-bold text-gray-900 text-sm">Add Vice Principal by Email</h3>
              </div>
              <p className="text-xs text-gray-500 mb-4 leading-relaxed">
                Enter the Google account / Gmail address of your Vice Principal. As soon as they sign in with this email, they will automatically gain full administrative access to manage classes, teachers, student approvals, and broadcasts.
              </p>
              
              <form onSubmit={handleAddVicePrincipal} className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      Email Address (Gmail / Google Account) <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <span className="material-icons-round absolute left-3 top-2.5 text-gray-400 text-sm">alternate_email</span>
                      <input
                        type="email"
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        placeholder="colleague@gmail.com"
                        required
                        disabled={isProcessing}
                        className="w-full pl-9 pr-3 py-2 bg-white border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-hidden transition-all disabled:opacity-50"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      Full Name (Optional)
                    </label>
                    <input
                      type="text"
                      value={nameInput}
                      onChange={(e) => setNameInput(e.target.value)}
                      placeholder="e.g. Dr. Sharma"
                      disabled={isProcessing}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-hidden transition-all disabled:opacity-50"
                    />
                  </div>
                </div>

                <div className="flex justify-end pt-1">
                  <button
                    type="submit"
                    disabled={isProcessing || !emailInput.trim()}
                    className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-xs hover:shadow-md transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isProcessing ? (
                      <>
                        <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <span className="material-icons-round text-sm">add_moderator</span>
                        <span>Add Vice Principal</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Current Administrators List */}
          <div className="space-y-3">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">
              Current Administration Team
            </h3>

            {/* Primary Principal Card (Permanent) */}
            <div className="bg-gradient-to-r from-amber-50/70 to-indigo-50/50 border border-amber-200/80 rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Avatar user={primaryAdmin || currentUser} className="w-11 h-11 border-2 border-amber-300" />
                  <span className="absolute -bottom-1 -right-1 bg-amber-500 text-white rounded-full p-0.5 shadow-xs" title="Primary Principal">
                    <span className="material-icons-round text-[11px] block">workspace_premium</span>
                  </span>
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-bold text-gray-900 text-sm">
                      {primaryAdmin?.name || currentUser.name}
                    </h4>
                    <span className="bg-amber-100 text-amber-800 text-[10px] font-black uppercase tracking-wide px-2 py-0.5 rounded-full border border-amber-200">
                      Primary Principal (Owner)
                    </span>
                    {primaryAdmin?.id === currentUser.id && (
                      <span className="text-[10px] text-gray-400 font-semibold">(You)</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5 font-mono">
                    {primaryAdmin?.email || currentUser.email}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-gray-400 italic hidden sm:inline">Permanent Administrator</span>
                {isPrimaryPrincipal && onTransferOwnership && (
                  <button
                    type="button"
                    onClick={() => setShowTransferModal(true)}
                    className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-bold transition-all shadow-xs flex items-center gap-1.5 cursor-pointer"
                    title="Transfer Primary Ownership to a successor"
                  >
                    <span className="material-icons-round text-sm">swap_horiz</span>
                    <span>Transfer Ownership</span>
                  </button>
                )}
              </div>
            </div>

            {/* Vice Principals List */}
            {adminList.length === 0 ? (
              <div className="bg-white border border-dashed border-gray-300 rounded-xl p-6 text-center text-gray-400 space-y-2">
                <span className="material-icons-round text-3xl text-gray-300">group_add</span>
                <p className="text-xs font-semibold text-gray-500">No Vice Principals added yet.</p>
                {isPrimaryPrincipal && (
                  <p className="text-[11px] text-gray-400">
                    Use the form above to add a colleague's email and delegate administrative power.
                  </p>
                )}
              </div>
            ) : (
              adminList.map((vp) => (
                <div
                  key={vp.email}
                  className="bg-white border border-gray-200 hover:border-gray-300 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-xs transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-indigo-50 border border-indigo-100 text-indigo-600 flex items-center justify-center font-bold text-base overflow-hidden flex-shrink-0">
                      {vp.photoUrl ? (
                        <img src={vp.photoUrl} alt={vp.name} className="w-full h-full object-cover" />
                      ) : (
                        <span>{vp.name?.charAt(0)?.toUpperCase() || vp.email.charAt(0).toUpperCase()}</span>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-bold text-gray-800 text-sm">{vp.name}</h4>
                        <span className="bg-indigo-100 text-indigo-700 text-[10px] font-bold px-2 py-0.5 rounded-full">
                          Vice Principal
                        </span>
                        {vp.isActive ? (
                          <span className="bg-emerald-100 text-emerald-800 text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                            Active
                          </span>
                        ) : (
                          <span className="bg-amber-100 text-amber-800 text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                            Awaiting First Sign-in
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 font-mono mt-0.5">{vp.email}</p>
                      {vp.addedAt && (
                        <p className="text-[10px] text-gray-400 mt-0.5">
                          Added {new Date(vp.addedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Actions (Only Primary Principal can remove) */}
                  <div className="flex items-center gap-2 self-end sm:self-center">
                    <button
                      type="button"
                      onClick={() => copyInvitationText(vp.email)}
                      className="px-2.5 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg flex items-center gap-1 transition-colors"
                      title="Copy invitation message to send to colleague"
                    >
                      <span className="material-icons-round text-xs">share</span>
                      <span className="hidden sm:inline">Invite Note</span>
                    </button>

                    {isPrimaryPrincipal && (
                      <button
                        type="button"
                        onClick={() => setRemovingEmail(vp.email)}
                        disabled={isProcessing}
                        className="px-2.5 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-700 text-xs font-bold rounded-lg flex items-center gap-1 transition-colors cursor-pointer"
                        title="Revoke administrative access"
                      >
                        <span className="material-icons-round text-xs">delete_outline</span>
                        <span>Remove</span>
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}

            {/* Ownership & Handover History Ledger */}
            {org.ownershipHistory && org.ownershipHistory.length > 0 && (
              <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
                    <span className="material-icons-round text-sm text-slate-500">history</span>
                    <span>Administrative Handover History ({org.ownershipHistory.length})</span>
                  </span>
                </div>
                <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                  {org.ownershipHistory.map((rec, i) => (
                    <div key={i} className="bg-white p-3 rounded-xl border border-slate-200 text-xs">
                      <div className="flex items-center justify-between text-[11px] text-slate-500 flex-wrap gap-1">
                        <span className={`px-1.5 py-0.2 rounded-md font-bold text-[9px] uppercase tracking-wide ${
                          rec.transferType === 'super_admin_reassignment' ? 'bg-amber-100 text-amber-800' : 'bg-indigo-100 text-indigo-800'
                        }`}>
                          {rec.transferType === 'super_admin_reassignment' ? 'Super Admin Recovery' : 'Self-Serve Handover'}
                        </span>
                        <span className="font-mono text-[10px]">{new Date(rec.transferredAt).toLocaleString()}</span>
                      </div>
                      <div className="mt-1 text-slate-800">
                        <span className="text-slate-400">Previous: </span>
                        <span className="font-semibold">{rec.previousAdminName || rec.previousAdminEmail}</span>
                        <span className="mx-1 text-slate-300">→</span>
                        <span className="text-slate-400">Successor: </span>
                        <span className="font-bold text-slate-900">{rec.newAdminName || rec.newAdminEmail}</span>
                      </div>
                      {(rec.reason || rec.referenceTicket) && (
                        <p className="text-[11px] text-slate-500 mt-0.5 italic">
                          {rec.reason && `Reason: ${rec.reason}. `}
                          {rec.referenceTicket && `Ref: ${rec.referenceTicket}`}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Explanatory Help Callout */}
          <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-4 text-xs text-slate-600 space-y-1.5">
            <p className="font-bold text-slate-800 flex items-center gap-1.5">
              <span className="material-icons-round text-slate-500 text-sm">help_outline</span>
              What can a Vice Principal do?
            </p>
            <p>
              Vice Principals hold complete administrative authority across {org.name}, including:
            </p>
            <ul className="list-disc list-inside space-y-0.5 pl-1 text-[11px] text-slate-500">
              <li>Reviewing and approving teachers, classes, and student enrollments</li>
              <li>Broadcasting official school announcements to classes and parents</li>
              <li>Managing academic years, smart calendar events, and attendance reports</li>
              <li>Sending teacher appreciation kudos and student recognitions</li>
            </ul>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="bg-gray-50 border-t border-gray-100 p-4 flex justify-between items-center">
          <div className="text-xs text-gray-500">
            Organization: <strong>{org.name}</strong>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 text-xs font-bold rounded-xl transition-colors cursor-pointer"
          >
            Done
          </button>
        </div>
      </div>

      {/* Confirmation Dialog for Removal */}
      {removingEmail && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/70 p-4 backdrop-blur-xs">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4 animate-scale-up border border-gray-100">
            <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto">
              <span className="material-icons-round text-2xl">person_remove</span>
            </div>
            <div className="text-center space-y-2">
              <h3 className="text-lg font-bold text-gray-900">Remove Vice Principal?</h3>
              <p className="text-xs text-gray-500 leading-relaxed">
                Are you sure you want to revoke administrative access for{' '}
                <strong className="text-gray-800 font-mono">{removingEmail}</strong>?
              </p>
              <p className="text-[11px] text-red-600 bg-red-50 p-2.5 rounded-lg font-medium">
                They will immediately lose access to the administrative console and school management tools.
              </p>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setRemovingEmail(null)}
                disabled={isProcessing}
                className="flex-1 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-xl transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleConfirmRemove(removingEmail)}
                disabled={isProcessing}
                className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-xl shadow-xs transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
              >
                {isProcessing ? (
                  <>
                    <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    <span>Removing...</span>
                  </>
                ) : (
                  <>
                    <span className="material-icons-round text-sm">delete</span>
                    <span>Confirm Removal</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Ownership Modal */}
      {showTransferModal && onTransferOwnership && (
        <TransferOwnershipModal
          isOpen={showTransferModal}
          onClose={() => setShowTransferModal(false)}
          org={org}
          currentUser={currentUser}
          allUsers={allUsers}
          onTransferOwnership={async (params) => {
            await onTransferOwnership(params);
            setShowTransferModal(false);
            onClose();
          }}
          t={t}
        />
      )}
    </div>
  );
};
